import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, ArrowUp, ArrowDown, Save, X, Star, ImagePlus } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { inputCls, labelCls, btnPrimary, btnDanger, PanelHeader } from "./_shared";
import ConfirmDialog from "@/components/site/ConfirmDialog";

// Utility: compress an uploaded image to a base64 data URL (max 1600px)
export async function fileToDataUrl(file, maxDim = 1600, quality = 0.82) {
  if (!file) return null;
  if (file.size > 8 * 1024 * 1024) throw new Error("Image too large (max 8MB before compression).");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

export default function GenericListPanel({
  endpoint,
  title,
  description,
  eyebrow,
  fields,
  emptyItem,
  testidPrefix,
  itemLabel = (it) => it.title || it.name || it.label,
  rowExtras,       // (item, {reload}) => ReactNode — extra buttons per row
  rowClassName,    // (item) => string — dynamic wrapper class
}) {
  const { adminApi } = useAuth();
  const { refresh } = useContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/${endpoint}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const remove = async () => {
    if (!pendingDelete) return;
    try {
      await adminApi.delete(`/${endpoint}/${pendingDelete.id}`);
      setItems((s) => s.filter((x) => x.id !== pendingDelete.id));
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingDelete(null);
    }
  };

  const move = async (id, dir) => {
    const idx = items.findIndex((x) => x.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    setItems(copy);
    try {
      await adminApi.post(`/${endpoint}/reorder`, { ids: copy.map((x) => x.id) });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  const startNew = () => setEditing({ ...emptyItem });
  const startEdit = (it) => setEditing({ ...it });
  const cancel = () => setEditing(null);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing };
      body.order = Number(body.order || 0);
      if (editing.id) {
        await adminApi.put(`/${endpoint}/${editing.id}`, body);
        toast.success("Saved");
      } else {
        await adminApi.post(`/${endpoint}`, body);
        toast.success("Created");
      }
      setEditing(null);
      await load();
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`${testidPrefix}-panel`}>
      <PanelHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={
          <button data-testid={`${testidPrefix}-new`} onClick={startNew} className={btnPrimary}>
            <Plus size={14} /> Add New
          </button>
        }
      />

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : (
        <div className="space-y-3">
          {items.length === 0 && <div className="text-white/50">No items yet.</div>}
          {items.map((it, i) => (
            <div
              key={it.id}
              data-testid={`${testidPrefix}-item-${it.id}`}
              className={`flex items-center gap-4 bg-[#1A1D21] border p-4 transition-colors ${
                rowClassName ? rowClassName(it) : "border-white/10"
              }`}
            >
              <div className="flex flex-col text-white/40">
                <button onClick={() => move(it.id, -1)} disabled={i === 0} className="hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move up">
                  <ArrowUp size={14} />
                </button>
                <button onClick={() => move(it.id, 1)} disabled={i === items.length - 1} className="hover:text-[#FF6B00] disabled:opacity-30" aria-label="Move down">
                  <ArrowDown size={14} />
                </button>
              </div>
              {(it.image_data || it.image_url) && (
                <img src={it.image_data || it.image_url} alt="" className="w-16 h-16 object-cover border border-white/10" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display uppercase tracking-wide text-lg truncate">{itemLabel(it)}</div>
                <div className="text-xs text-white/50 truncate mt-1">
                  {it.description || it.quote || it.alt || ""}
                </div>
              </div>
              {rowExtras && rowExtras(it, { reload: load })}
              <button
                data-testid={`${testidPrefix}-edit-${it.id}`}
                onClick={() => startEdit(it)}
                className="text-white/60 hover:text-[#FF6B00] text-sm font-display tracking-widest uppercase"
              >
                Edit
              </button>
              <button
                data-testid={`${testidPrefix}-delete-${it.id}`}
                onClick={() => setPendingDelete(it)}
                className={btnDanger}
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          fields={fields}
          value={editing}
          onChange={setEditing}
          onCancel={cancel}
          onSave={save}
          saving={saving}
          testidPrefix={testidPrefix}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this item?"
        description={
          pendingDelete
            ? `"${itemLabel(pendingDelete)}" will be permanently removed from the site.`
            : ""
        }
        confirmLabel="Delete"
        onConfirm={remove}
        testid={`${testidPrefix}-delete-dialog`}
      />
    </div>
  );
}

function EditModal({ fields, value, onChange, onCancel, onSave, saving, testidPrefix }) {
  const setF = (k, v) => onChange({ ...value, [k]: v });
  const fileRef = useRef();
  const [processingImage, setProcessingImage] = useState(false);

  const handleFile = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    setProcessingImage(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      // Single state update — writing image_data + clearing image_url in one call
      // (two separate setF calls share a stale `value` and stomp each other)
      onChange({ ...value, image_data: dataUrl, image_url: "" });
    } catch (e) {
      toast.error(e.message || "Image upload failed — try a JPG or PNG under 8MB");
      if (fileRef.current) fileRef.current.value = "";
    } finally {
      setProcessingImage(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    // Single update: setting URL implicitly clears any previously uploaded base64
    onChange({ ...value, image_url: url, image_data: url ? null : value.image_data });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid={`${testidPrefix}-modal`}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className="w-full max-w-2xl bg-[#1A1D21] border border-white/10 my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="font-display uppercase tracking-wider text-xl">{value.id ? "Edit Item" : "New Item"}</div>
          <button onClick={onCancel} className="text-white/60 hover:text-white" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {fields.map((f) => (
            <div key={f.name}>
              <label className={labelCls}>{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  className={`${inputCls} min-h-[120px]`}
                  value={value[f.name] || ""}
                  onChange={(e) => setF(f.name, e.target.value)}
                />
              ) : f.type === "image" ? (
                <div className="space-y-3">
                  {(value.image_data || value.image_url) && (
                    <img
                      src={value.image_data || value.image_url}
                      alt=""
                      data-testid={`${testidPrefix}-field-${f.name}-preview`}
                      className="w-full max-h-64 object-cover border border-white/10"
                    />
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    data-testid={`${testidPrefix}-field-${f.name}`}
                    onChange={handleFile}
                    disabled={processingImage}
                    className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#FF6B00] file:text-white file:font-display file:uppercase file:tracking-widest file:text-xs hover:file:bg-[#E65A00] cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                  />
                  {processingImage && (
                    <div className="text-xs text-[#FF6B00] flex items-center gap-2" data-testid={`${testidPrefix}-field-${f.name}-processing`}>
                      <Loader2 size={12} className="animate-spin" /> Processing image…
                    </div>
                  )}
                  <div className="text-xs text-white/40">Or paste an image URL below:</div>
                  <input
                    className={inputCls}
                    data-testid={`${testidPrefix}-field-${f.name}-url`}
                    placeholder="https://…"
                    value={value.image_url || ""}
                    onChange={handleUrlChange}
                  />
                </div>
              ) : f.type === "multi-image" ? (
                <MultiImageField
                  value={Array.isArray(value.photos) ? value.photos : []}
                  onChange={(photos) => {
                    const cover = photos[0] || {};
                    onChange({
                      ...value,
                      photos,
                      image_data: cover.image_data || null,
                      image_url: cover.image_url || "",
                    });
                  }}
                  testidPrefix={`${testidPrefix}-field-${f.name}`}
                />
              ) : f.type === "select" ? (
                <select
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  className={inputCls}
                  value={value[f.name] || ""}
                  onChange={(e) => setF(f.name, e.target.value)}
                >
                  <option value="">— Select —</option>
                  {f.options.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={`${testidPrefix}-field-${f.name}`}
                  type={f.type || "text"}
                  className={inputCls}
                  value={value[f.name] ?? ""}
                  onChange={(e) => setF(f.name, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-white/10">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white">Cancel</button>
          <button data-testid={`${testidPrefix}-modal-save`} onClick={onSave} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}


function MultiImageField({ value, onChange, testidPrefix }) {
  const photos = value || [];
  const fileRef = useRef();
  const [processing, setProcessing] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const setPhotos = (next) => onChange(next);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setProcessing(true);
    const next = [...photos];
    let failed = 0;
    for (const f of files) {
      try {
        const dataUrl = await fileToDataUrl(f);
        next.push({ image_data: dataUrl, image_url: "" });
      } catch {
        failed += 1;
      }
    }
    setPhotos(next);
    setProcessing(false);
    if (fileRef.current) fileRef.current.value = "";
    if (failed) toast.error(`${failed} photo${failed > 1 ? "s" : ""} skipped (too large or unreadable — max 8MB each)`);
    else if (files.length) toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added`);
  };

  const addUrl = () => {
    const url = urlInput.trim();
    if (!url) return;
    setPhotos([...photos, { image_url: url, image_data: null }]);
    setUrlInput("");
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    setPhotos(next);
  };

  const swap = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= photos.length) return;
    const next = [...photos];
    [next[idx], next[j]] = [next[j], next[idx]];
    setPhotos(next);
  };

  const makeCover = (idx) => {
    if (idx === 0) return;
    const next = [...photos];
    const [chosen] = next.splice(idx, 1);
    next.unshift(chosen);
    setPhotos(next);
  };

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      {photos.length === 0 && (
        <div className="text-sm text-white/50 border border-dashed border-white/15 p-6 text-center">
          No photos yet. Add one or more below — the first photo becomes the cover shown on the tile.
        </div>
      )}

      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid={`${testidPrefix}-grid`}>
          {photos.map((p, idx) => {
            const src = p.image_data || p.image_url;
            return (
              <div
                key={idx}
                data-testid={`${testidPrefix}-photo-${idx}`}
                className={`relative group border ${idx === 0 ? "border-[#FF6B00]" : "border-white/10"} bg-[#121212] aspect-square`}
              >
                {src ? (
                  <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs">No image</div>
                )}

                {idx === 0 && (
                  <div className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 bg-[#FF6B00] px-2 py-0.5 text-[9px] font-display tracking-widest uppercase text-white">
                    <Star size={9} className="fill-white" /> Cover
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 bg-black/70 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex items-center justify-between px-1 py-1">
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => swap(idx, -1)}
                      disabled={idx === 0}
                      className="p-1 text-white/80 hover:text-[#FF6B00] disabled:opacity-30"
                      aria-label="Move left"
                      data-testid={`${testidPrefix}-photo-${idx}-left`}
                    >
                      <ArrowUp size={12} className="-rotate-90" />
                    </button>
                    <button
                      type="button"
                      onClick={() => swap(idx, 1)}
                      disabled={idx === photos.length - 1}
                      className="p-1 text-white/80 hover:text-[#FF6B00] disabled:opacity-30"
                      aria-label="Move right"
                      data-testid={`${testidPrefix}-photo-${idx}-right`}
                    >
                      <ArrowDown size={12} className="-rotate-90" />
                    </button>
                    {idx !== 0 && (
                      <button
                        type="button"
                        onClick={() => makeCover(idx)}
                        className="p-1 text-white/80 hover:text-[#FF6B00]"
                        aria-label="Set as cover"
                        title="Set as cover"
                        data-testid={`${testidPrefix}-photo-${idx}-cover`}
                      >
                        <Star size={12} />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="p-1 text-white/80 hover:text-red-500"
                    aria-label="Remove"
                    data-testid={`${testidPrefix}-photo-${idx}-remove`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          data-testid={`${testidPrefix}-file`}
          onChange={handleFiles}
          disabled={processing}
          className="block w-full text-sm text-white/70 file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#FF6B00] file:text-white file:font-display file:uppercase file:tracking-widest file:text-xs hover:file:bg-[#E65A00] cursor-pointer disabled:opacity-50 disabled:cursor-wait"
        />
        {processing && (
          <div className="text-xs text-[#FF6B00] flex items-center gap-2" data-testid={`${testidPrefix}-processing`}>
            <Loader2 size={12} className="animate-spin" /> Processing photos…
          </div>
        )}
        <div className="text-xs text-white/40 pt-1">Tip — hold Ctrl/⌘ to select multiple photos at once.</div>
      </div>

      <div className="flex gap-2 pt-2 border-t border-white/10">
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Or paste a photo URL: https://…"
          className={`${inputCls} flex-1`}
          data-testid={`${testidPrefix}-url-input`}
        />
        <button
          type="button"
          onClick={addUrl}
          disabled={!urlInput.trim()}
          className={`${btnPrimary} disabled:opacity-40`}
          data-testid={`${testidPrefix}-url-add`}
        >
          <ImagePlus size={14} /> Add URL
        </button>
      </div>
    </div>
  );
}
